import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { AccountReportPageHeader } from "@/components/accounts/AccountReportShell";
import { ChartOfAccountLeafSelect } from "@/components/accounts/ChartOfAccountLeafSelect";
import { PartySelect } from "@/components/catalog/PartySelect";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import {
  emptyPaymentMethodDraft,
  PaymentMethodFieldsGroup,
  paymentMethodUsesBank,
  type PaymentMethodDraft,
} from "@/components/sales/PaymentMethodFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { MoneyAmountInput } from "@/components/sales/MoneyAmountInput";
import { money } from "@/lib/accounts/ledgerReportUtils";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import {
  createManualVoucher,
  emptyVoucherDraft,
  emptyVoucherLine,
  listVoucherTypes,
  suggestedCounterpartyTypeForAccount,
  voucherTypeUsesPayment,
  type ManualVoucherDraft,
  type VoucherCounterpartyType,
  type VoucherTypeOption,
} from "@/lib/accounts/manualVoucherStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import type { ChartOfAccountNode } from "@/lib/dataBank/chartOfAccountsStore";
import { listPartyOptions, syncPartiesFromApi, type PartyOption } from "@/lib/parties/partiesStore";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { format } from "date-fns";
import { ArrowLeft, FileText, Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const COUNTERPARTY_TYPE_OPTIONS: { value: VoucherCounterpartyType; label: string }[] = [
  { value: "customer", label: "Customer" },
  { value: "supplier", label: "Supplier" },
  { value: "investor", label: "Investor" },
];

function partyMatchesType(party: PartyOption, type: VoucherCounterpartyType | ""): boolean {
  if (!type) return true;
  const t = (party.type ?? "").toLowerCase();
  return t === type || t === "all" || t === "both";
}

function voucherErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string" && data.message.trim()) return data.message;
  }
  return "Could not post voucher. Please try again.";
}

function defaultPaymentForVoucherType(code: string): PaymentMethodDraft {
  const c = code.toUpperCase();
  if (c === "CPV" || c === "CRV") {
    return emptyPaymentMethodDraft({ paymentMethod: "cash" });
  }
  if (c === "BPV" || c === "BRV") {
    return emptyPaymentMethodDraft({ paymentMethod: "online" });
  }
  return emptyPaymentMethodDraft();
}

const VoucherCreate = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "chart_of_account_can_view_any") || can(user, "bank_can_view_any");

  const [voucherTypes, setVoucherTypes] = useState<VoucherTypeOption[]>([]);
  const [parties, setParties] = useState<PartyOption[]>(() => listPartyOptions());
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [draft, setDraft] = useState<ManualVoucherDraft>(() =>
    emptyVoucherDraft(format(new Date(), "yyyy-MM-dd"), "JV"),
  );
  const [saving, setSaving] = useState(false);

  const selectedVoucherType = useMemo(
    () => voucherTypes.find((t) => t.code === draft.voucherTypeCode) ?? null,
    [voucherTypes, draft.voucherTypeCode],
  );

  const showPaymentFields = voucherTypeUsesPayment(
    selectedVoucherType?.nature,
    draft.voucherTypeCode,
  );

  const totals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    for (const line of draft.lines) {
      const d = line.debit === "" ? 0 : Number(line.debit);
      const c = line.credit === "" ? 0 : Number(line.credit);
      if (Number.isFinite(d)) debit += d;
      if (Number.isFinite(c)) credit += c;
    }
    debit = Math.round(debit * 100) / 100;
    credit = Math.round(credit * 100) / 100;
    return {
      debit,
      credit,
      balanced: Math.abs(debit - credit) < 0.009 && debit > 0,
      difference: Math.round((debit - credit) * 100) / 100,
    };
  }, [draft.lines]);

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void listVoucherTypes()
      .then((rows) => {
        if (cancelled) return;
        setVoucherTypes(rows);
        setDraft((d) => {
          if (rows.length === 0 || rows.some((r) => r.code === d.voucherTypeCode)) return d;
          const fallback = rows.find((r) => r.code === "JV") ?? rows[0];
          return {
            ...d,
            voucherTypeCode: fallback.code,
            payment: defaultPaymentForVoucherType(fallback.code),
          };
        });
      })
      .catch(() => {
        if (!cancelled) toast.error("Could not load voucher types.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void syncPartiesFromApi()
      .then(() => {
        if (!cancelled) setParties(listPartyOptions());
      })
      .catch(() => {
        /* keep cached */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchBanks()
      .then((rows) => {
        if (!cancelled) setBanks(rows);
      })
      .catch(() => {
        if (!cancelled) setBanks([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const updateLine = (key: string, patch: Partial<(typeof draft.lines)[number]>) => {
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    }));
  };

  const lineAmount = (line: (typeof draft.lines)[number], side: "debit" | "credit") => {
    const raw = line[side];
    if (raw === "") return 0;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
  };

  const handleAccountChange = (key: string, id: string, account?: ChartOfAccountNode | null) => {
    const code = account?.code ?? "";
    const name = account?.name ?? "";
    const suggested = id ? suggestedCounterpartyTypeForAccount(code, name) : null;

    setDraft((d) => {
      const target = d.lines.find((line) => line.key === key);
      if (!target) return d;

      const hasAmount = lineAmount(target, "debit") > 0 || lineAmount(target, "credit") > 0;
      let debit = target.debit;
      let credit = target.credit;

      // When picking an account on an empty amount line, fill the side needed to balance.
      if (id && !hasAmount) {
        let otherDebit = 0;
        let otherCredit = 0;
        for (const line of d.lines) {
          if (line.key === key) continue;
          otherDebit += lineAmount(line, "debit");
          otherCredit += lineAmount(line, "credit");
        }
        const gap = Math.round((otherDebit - otherCredit) * 100) / 100;
        if (gap > 0) {
          debit = "";
          credit = String(gap);
        } else if (gap < 0) {
          debit = String(Math.abs(gap));
          credit = "";
        }
      }

      return {
        ...d,
        lines: d.lines.map((line) =>
          line.key === key
            ? {
                ...line,
                chartOfAccountsId: id,
                accountCode: code,
                accountName: name,
                partyType: suggested ?? "",
                partyId: "",
                debit,
                credit,
              }
            : line,
        ),
      };
    });
  };

  const addLine = () => {
    setDraft((d) => ({ ...d, lines: [...d.lines, emptyVoucherLine()] }));
  };

  const removeLine = (key: string) => {
    setDraft((d) => {
      if (d.lines.length <= 2) return d;
      return { ...d, lines: d.lines.filter((line) => line.key !== key) };
    });
  };

  const resetDraft = () => {
    const code = draft.voucherTypeCode || "JV";
    setDraft({
      ...emptyVoucherDraft(format(new Date(), "yyyy-MM-dd"), code),
      payment: defaultPaymentForVoucherType(code),
    });
  };

  const handleVoucherTypeChange = (code: string) => {
    setDraft((d) => ({
      ...d,
      voucherTypeCode: code,
      payment: voucherTypeUsesPayment(
        voucherTypes.find((t) => t.code === code)?.nature,
        code,
      )
        ? defaultPaymentForVoucherType(code)
        : emptyPaymentMethodDraft(),
    }));
  };

  const handlePost = async () => {
    if (!draft.date) {
      toast.error("Voucher date is required.");
      return;
    }
    if (!draft.voucherTypeCode) {
      toast.error("Select a voucher type.");
      return;
    }
    if (!totals.balanced) {
      toast.error(
        totals.debit <= 0
          ? "Enter debit and credit amounts."
          : `Voucher is not balanced (difference ${money(Math.abs(totals.difference))}).`,
      );
      return;
    }

    if (showPaymentFields) {
      if (!draft.payment.paymentMethod) {
        toast.error("Select a payment method.");
        return;
      }
      if (paymentMethodUsesBank(draft.payment.paymentMethod) && !draft.payment.bankId) {
        toast.error("Select a bank for this payment method.");
        return;
      }
    }

    const filled = draft.lines.filter(
      (line) =>
        line.chartOfAccountsId &&
        ((line.debit !== "" && Number(line.debit) > 0) || (line.credit !== "" && Number(line.credit) > 0)),
    );
    if (filled.length < 2) {
      toast.error("At least two lines with account and amount are required.");
      return;
    }

    for (const line of filled) {
      const role = suggestedCounterpartyTypeForAccount(line.accountCode, line.accountName);
      const type = line.partyType || role;
      if (!type) continue;
      if (!line.partyId) {
        toast.error(`Select a ${type} for account "${line.accountName || line.accountCode}".`);
        return;
      }
    }

    setSaving(true);
    try {
      const created = await createManualVoucher({
        ...draft,
        payment: showPaymentFields ? draft.payment : emptyPaymentMethodDraft(),
        lines: filled,
      });
      toast.success(created.voucherNumber ? `Posted ${created.voucherNumber}` : "Voucher posted.");
      navigate("/accounts/vouchers");
    } catch (error) {
      toast.error(voucherErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return (
      <Layout>
        <PermissionDenied />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5">
        <AccountReportPageHeader
          icon={<FileText className="h-5 w-5 text-primary" />}
          title="Create voucher"
          description="Post a manual voucher (double entry). Debits must equal credits."
          actions={
            <Button asChild variant="outline" size="sm" className="h-8 text-xs">
              <Link to="/accounts/vouchers">
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                Back to list
              </Link>
            </Button>
          }
        />

        <div className="rounded-2xl border border-border/55 bg-card/80 p-4 shadow-sm ring-1 ring-border/25 space-y-4">
          <div
            className={cn(
              "grid grid-cols-1 gap-3 sm:grid-cols-2",
              showPaymentFields ? "xl:grid-cols-4" : "xl:grid-cols-3",
            )}
          >
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Voucher type *</Label>
              <Select value={draft.voucherTypeCode} onValueChange={handleVoucherTypeChange}>
                <SelectTrigger className="glass-input h-9 text-xs">
                  <SelectValue placeholder="Select voucher type" />
                </SelectTrigger>
                <SelectContent>
                  {voucherTypes.map((opt) => (
                    <SelectItem key={opt.code} value={opt.code}>
                      {opt.code} — {opt.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Voucher date *</Label>
              <DatePartsPicker
                value={draft.date}
                onChange={(val) => setDraft((d) => ({ ...d, date: val }))}
              />
            </div>

            {showPaymentFields ? (
              <PaymentMethodFieldsGroup
                value={draft.payment}
                onChange={(next) => setDraft((d) => ({ ...d, payment: next }))}
                banks={banks}
                allowNone={false}
                required
                compact
                showDetails={false}
                label="Payment method *"
                labelClassName="text-xs font-semibold text-foreground"
                triggerClassName="glass-input h-9 text-xs"
                inputClassName="glass-input h-9 text-xs"
              />
            ) : null}

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Narration</Label>
              <Input
                className="h-9 text-xs"
                placeholder="Optional description"
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              />
            </div>
          </div>

          {showPaymentFields && paymentMethodUsesBank(draft.payment.paymentMethod) ? (
            <PaymentMethodFieldsGroup
              value={draft.payment}
              onChange={(next) => setDraft((d) => ({ ...d, payment: next }))}
              banks={banks}
              allowNone={false}
              required
              compact
              methodWithBankRow
              showSelect={false}
              labelClassName="text-xs font-semibold text-foreground"
              triggerClassName="glass-input h-9 text-xs"
              inputClassName="glass-input h-9 text-xs"
              className="rounded-xl border border-border/40 bg-background/40 p-3"
            />
          ) : null}

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-foreground">Journal lines</h3>
              <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={addLine}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Add line
              </Button>
            </div>

            <div className="overflow-hidden rounded-xl border border-border/55">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-10 text-xs">#</TableHead>
                    <TableHead className="min-w-[200px] text-xs">Account *</TableHead>
                    <TableHead className="min-w-[120px] text-xs">Type</TableHead>
                    <TableHead className="min-w-[160px] text-xs">Party</TableHead>
                    <TableHead className="min-w-[110px] text-xs text-right">Debit</TableHead>
                    <TableHead className="min-w-[110px] text-xs text-right">Credit</TableHead>
                    <TableHead className="min-w-[140px] text-xs">Line narration</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {draft.lines.map((line, index) => {
                    const suggested = suggestedCounterpartyTypeForAccount(line.accountCode, line.accountName);
                    const needsCounterparty = suggested != null;
                    const effectiveType = (line.partyType || suggested || "") as VoucherCounterpartyType | "";
                    const partyOptions = parties.filter((p) => partyMatchesType(p, effectiveType));

                    return (
                      <TableRow key={line.key}>
                        <TableCell className="text-xs text-muted-foreground tabular-nums align-top">
                          <div className="flex h-3.5 items-center" />
                          <div className="flex h-9 items-center">{index + 1}</div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex h-3.5 items-center" />
                          <ChartOfAccountLeafSelect
                            value={line.chartOfAccountsId}
                            onChange={(id, account) => handleAccountChange(line.key, id, account)}
                            triggerClassName="h-9 text-xs"
                          />
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex h-3.5 items-center" />
                          {needsCounterparty ? (
                            <Select
                              value={effectiveType || undefined}
                              onValueChange={(v) =>
                                updateLine(line.key, {
                                  partyType: v as VoucherCounterpartyType,
                                  partyId: "",
                                })
                              }
                            >
                              <SelectTrigger className="glass-input h-9 text-xs">
                                <SelectValue placeholder="Type" />
                              </SelectTrigger>
                              <SelectContent>
                                {COUNTERPARTY_TYPE_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="flex h-9 items-center">
                              <span className="text-xs text-muted-foreground px-1">—</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex h-3.5 items-center" />
                          {needsCounterparty ? (
                            <PartySelect
                              value={line.partyId}
                              options={partyOptions}
                              onChange={(id) => updateLine(line.key, { partyId: id })}
                              placeholder={
                                effectiveType
                                  ? `Select ${effectiveType}`
                                  : "Select party"
                              }
                              triggerClassName="h-9 text-xs"
                            />
                          ) : (
                            <div className="flex h-9 items-center">
                              <span className="text-xs text-muted-foreground px-1">—</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="min-w-[7.5rem]">
                            <div className="flex h-3.5 items-center justify-end px-0.5">
                              {line.debit !== "" && Number(line.debit) > 0 && pkrAmountShortLabel(Number(line.debit)) ? (
                                <span className="text-[10px] font-medium leading-none text-primary tabular-nums whitespace-nowrap">
                                  {pkrAmountShortLabel(Number(line.debit))}
                                </span>
                              ) : null}
                            </div>
                            <MoneyAmountInput
                              value={line.debit === "" ? 0 : Number(line.debit)}
                              onChange={(next) =>
                                updateLine(line.key, {
                                  debit: next > 0 ? String(next) : "",
                                  ...(next > 0 ? { credit: "" } : {}),
                                })
                              }
                              showShortLabel={false}
                              size="sm"
                              inputClassName="h-9 w-full min-w-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input"
                              className="items-stretch"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="min-w-[7.5rem]">
                            <div className="flex h-3.5 items-center justify-end px-0.5">
                              {line.credit !== "" && Number(line.credit) > 0 && pkrAmountShortLabel(Number(line.credit)) ? (
                                <span className="text-[10px] font-medium leading-none text-primary tabular-nums whitespace-nowrap">
                                  {pkrAmountShortLabel(Number(line.credit))}
                                </span>
                              ) : null}
                            </div>
                            <MoneyAmountInput
                              value={line.credit === "" ? 0 : Number(line.credit)}
                              onChange={(next) =>
                                updateLine(line.key, {
                                  credit: next > 0 ? String(next) : "",
                                  ...(next > 0 ? { debit: "" } : {}),
                                })
                              }
                              showShortLabel={false}
                              size="sm"
                              inputClassName="h-9 w-full min-w-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input"
                              className="items-stretch"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex h-3.5 items-center" />
                          <Input
                            className="h-9 text-xs"
                            placeholder="Optional"
                            value={line.description}
                            onChange={(e) => updateLine(line.key, { description: e.target.value })}
                          />
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex h-3.5 items-center" />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-8 text-muted-foreground hover:text-destructive"
                            disabled={draft.lines.length <= 2}
                            onClick={() => removeLine(line.key)}
                            aria-label="Remove line"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-1">
              {totals.debit > 0 || totals.credit > 0 ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                  <span className="tabular-nums">
                    Debit: <strong>{money(totals.debit)}</strong>
                    {pkrAmountShortLabel(totals.debit) ? (
                      <span className="ml-1 text-primary">({pkrAmountShortLabel(totals.debit)})</span>
                    ) : null}
                  </span>
                  <span className="tabular-nums">
                    Credit: <strong>{money(totals.credit)}</strong>
                    {pkrAmountShortLabel(totals.credit) ? (
                      <span className="ml-1 text-primary">({pkrAmountShortLabel(totals.credit)})</span>
                    ) : null}
                  </span>
                  <span
                    className={cn(
                      "font-medium",
                      totals.balanced ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400",
                    )}
                  >
                    {totals.balanced
                      ? "Balanced"
                      : `Out of balance by ${money(Math.abs(totals.difference))}`}
                  </span>
                </div>
              ) : (
                <div />
              )}
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" className="h-9" onClick={resetDraft} disabled={saving}>
                  Clear
                </Button>
                <Button
                  type="button"
                  variant="premium"
                  size="sm"
                  className="h-9"
                  onClick={() => void handlePost()}
                  disabled={saving || !totals.balanced}
                >
                  {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <FileText className="mr-1.5 h-3.5 w-3.5" />}
                  Post voucher
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default VoucherCreate;
